import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { Language } from "./types";

const en = {
  "app.name": "Deskflow",
  "app.tagline": "Your Smart Office Assistant",
  "nav.dashboard": "Overview",
  "nav.meetings": "Meetings",
  "nav.calendar": "Calendar & To-Do",
  "nav.translate": "Translation",
  "nav.skills": "Skills",
  "nav.search": "Search",
  "nav.settings": "AI settings",
  "nav.privacy": "Privacy & data",
  "nav.local": "Local-first",
  "nav.localDetail": "Your workspace stays in this browser",
  "nav.workspace": "Personal workspace",
  "nav.plan": "No account required",
  "header.private": "PRIVATE BY DESIGN",
  "header.dashboard": "Good morning",
  "header.meetings": "Meeting workspace",
  "header.calendar": "Calendar & to-do",
  "header.translate": "Translation workspace",
  "header.skills": "Reusable skills",
  "header.search": "Smart search",
  "header.settings": "Bring your own AI",
  "header.privacy": "Your data, your control",
  "subtitle.dashboard": "Here’s what deserves your attention today.",
  "subtitle.meetings": "Turn transcripts into clear, reviewable next steps.",
  "subtitle.calendar": "Plan schedules and finish tasks from one local view.",
  "subtitle.translate":
    "Choose an explicit language pair and preview a mock translation.",
  "subtitle.skills": "Run repeatable prompts without rebuilding your workflow.",
  "subtitle.search":
    "Explore the complete search experience with mock AI results.",
  "subtitle.settings": "Connect your provider directly from this browser.",
  "subtitle.privacy":
    "See exactly what is stored and remove it whenever you want.",
  "language.label": "Language",
  "language.english": "English",
  "language.chinese": "简体中文",
  "onboarding.eyebrow": "WELCOME TO DESKFLOW",
  "onboarding.title": "Choose how you want to explore AI",
  "onboarding.description":
    "Deskflow is opening access in stages. Choose Demo Mode to explore the workspace today.",
  "onboarding.byok": "Use My Own API Key (BYOK)",
  "onboarding.byokDescription":
    "Connect your preferred compatible AI provider directly.",
  "onboarding.hosted": "Use Platform Hosted API",
  "onboarding.hostedDescription":
    "Use Deskflow without managing provider credentials.",
  "onboarding.demo": "Demo Mode / Explore First",
  "onboarding.demoDescription":
    "Enter the workspace and preview the product experience.",
  "onboarding.comingSoon": "Coming Soon / 内测中",
  "onboarding.tooltip": "Coming Soon",
  "onboarding.privacy": "Your selection is stored only in this browser.",
  "demo.aiUnavailable":
    "Please wait for the hosted API or configure your own key in settings once available.",
  "dashboard.timeSaved": "Estimated time saved",
  "dashboard.meetings": "Meetings saved",
  "dashboard.meetingUnit": "meetings",
  "dashboard.actions": "Action items",
  "dashboard.processing": "Processing mode",
  "dashboard.aiSpend": "AI Spend",
  "dashboard.demoMode": "Demo mode",
  "dashboard.minutes": "minutes",
  "dashboard.local": "Local preview",
  "dashboard.ai": "Your AI provider",
  "dashboard.recent": "Recent meetings",
  "dashboard.viewAll": "View all",
  "dashboard.emptyTitle": "Your workspace is ready",
  "dashboard.emptyBody":
    "Import your first transcript to create structured minutes and action items.",
  "dashboard.newMeeting": "New meeting",
  "dashboard.actionsTitle": "Action items",
  "dashboard.noActions": "No open actions yet.",
  "dashboard.markDone": "Mark complete",
  "meeting.new": "New meeting",
  "meeting.saved": "Saved meetings",
  "meeting.search": "Search meetings",
  "meeting.searchPlaceholder": "Search titles or summaries…",
  "meeting.none": "No meetings match your search.",
  "meeting.title": "Meeting title",
  "meeting.titlePlaceholder": "e.g. Weekly product review",
  "meeting.transcript": "Transcript",
  "meeting.transcriptPlaceholder":
    "Paste your meeting transcript here, or import a text file…",
  "meeting.import": "Import file",
  "meeting.generate": "Generate minutes",
  "meeting.generating": "Creating minutes…",
  "meeting.smartExtract": "Smart Extract",
  "meeting.smartExtractHelp":
    "Recognize action items, owners, and deadlines with local mock logic.",
  "meeting.extractButton": "Extract entities",
  "meeting.extractRequired": "Add some transcript text before extracting.",
  "meeting.extracted": "Mock entities extracted. Review and edit them below.",
  "meeting.mockEntities": "MOCK RECOGNITION",
  "meeting.extractedItems": "Extracted action items",
  "meeting.action": "Action item",
  "meeting.owner": "Owner",
  "meeting.deadline": "Deadline",
  "meeting.addItem": "Add item",
  "meeting.addToTodoList": "Add to To-Do List",
  "meeting.addedToTodos":
    "Extracted items were added to your local to-do list.",
  "meeting.mockHint":
    "Demo processing uses deterministic mock logic and never calls an external API.",
  "meeting.localHint":
    "Demo mode is active. AI generation will unlock as access expands.",
  "meeting.aiHint":
    "Content will be sent directly to your configured AI provider.",
  "meeting.required": "Add a title and at least 20 characters of transcript.",
  "meeting.created": "Meeting minutes saved locally.",
  "meeting.failed": "Minutes could not be generated.",
  "meeting.summary": "Executive summary",
  "meeting.decisions": "Decisions",
  "meeting.actions": "Action items",
  "meeting.risks": "Risks and blockers",
  "meeting.noDecisions": "No clear decisions detected.",
  "meeting.noRisks": "No clear risks detected.",
  "meeting.unassigned": "Unassigned",
  "meeting.review":
    "AI-assisted minutes can be wrong. Review them before sharing.",
  "meeting.delete": "Delete meeting",
  "meeting.deleteConfirm":
    "Delete this meeting and its transcript from this browser?",
  "meeting.export": "Export Markdown",
  "meeting.back": "Back to meetings",
  "meeting.source": "Source",
  "meeting.localGenerated": "Generated locally",
  "meeting.aiGenerated": "Generated with your AI provider",
  "meeting.accept": "Accept",
  "meeting.edit": "Edit",
  "meeting.ignore": "Ignore",
  "meeting.saveEdit": "Save edit",
  "meeting.addToTodo": "Add to To-Do",
  "meeting.addedToTodo": "Added to To-Do",
  "meeting.reviewSummary": "Review summary",
  "meeting.summaryAdded": "Summary review task added to your local to-do list.",
  "meeting.status.pending": "Needs review",
  "meeting.status.accepted": "Accepted",
  "meeting.status.ignored": "Ignored",
  "calendar.today": "Today",
  "calendar.zoom": "Calendar zoom",
  "calendar.zoomIn": "Zoom in",
  "calendar.zoomOut": "Zoom out",
  "calendar.selectedDay": "SELECTED DAY",
  "calendar.empty": "Nothing scheduled for this day.",
  "calendar.complete": "Mark complete",
  "calendar.allDay": "All day",
  "calendar.add": "Quick add",
  "calendar.titlePlaceholder": "Task or schedule title",
  "calendar.ownerPlaceholder": "Owner",
  "calendar.todo": "To-do",
  "calendar.schedule": "Schedule",
  "calendar.addButton": "Add locally",
  "calendar.saved": "Saved to this browser.",
  "translate.pair": "Language pair",
  "translate.enZh": "English to Chinese",
  "translate.zhEn": "Chinese to English",
  "translate.swap": "Swap",
  "translate.source": "Source text",
  "translate.result": "Mock translation",
  "translate.placeholder": "Paste text to translate…",
  "translate.empty": "Your mock translation will appear here.",
  "translate.translate": "Translate with mock",
  "translate.clear": "Clear",
  "translate.copy": "Copy result",
  "translate.copied": "Translation copied.",
  "translate.mockNotice": "Mock translation completed locally.",
  "translate.localOnly": "LOCAL MOCK HISTORY",
  "translate.history": "Recent translations",
  "translate.noHistory": "No translations yet.",
  "skills.starter": "Starter skills",
  "skills.input": "Input",
  "skills.inputPlaceholder": "Paste the text you want to work with…",
  "skills.run": "Run skill",
  "skills.running": "Running…",
  "skills.output": "Result",
  "skills.choose": "Choose a skill to get started.",
  "skills.localNotice":
    "This result is generated by local mock logic. No API request is made.",
  "skills.mockBadge": "LOCAL MOCK",
  "skills.useExample": "Use example text",
  "skills.summarize.name": "Summarize a document",
  "skills.summarize.description":
    "Condense long business text into the essential points.",
  "skills.email.name": "Rewrite an email",
  "skills.email.description":
    "Make an email concise, professional, and friendly.",
  "skills.actions.name": "Extract action items",
  "skills.actions.description":
    "Find tasks, owners, and dates in unstructured notes.",
  "skills.report.name": "Create a weekly report",
  "skills.report.description":
    "Turn raw updates into a clear weekly status report.",
  "skills.translate.name": "Translate business text",
  "skills.translate.description":
    "Translate text while preserving professional tone.",
  "skills.agenda.name": "Prepare a meeting agenda",
  "skills.agenda.description":
    "Build an agenda from goals and background notes.",
  "search.label": "Search keyword",
  "search.placeholder": "Search a topic, company, or workflow…",
  "search.button": "Search",
  "search.helper":
    "Results and AI summaries are hardcoded mock content for workflow testing.",
  "search.recent": "Recent",
  "search.emptyTitle": "Search the demo knowledge space",
  "search.emptyBody":
    "Enter any keyword to preview result cards and mock AI summaries.",
  "search.mockLabel": "MOCK SEARCH RESULTS",
  "search.resultsFor": "Results for",
  "search.aiSummary": "AI summary",
  "search.more": "Show mock detail",
  "search.less": "Hide detail",
  "search.mockDetail":
    "This expanded section is intentionally local mock content. A live search provider can replace it later without changing the interface.",
  "settings.provider": "AI provider",
  "settings.comingSoon":
    "BYOK is currently in private beta. These controls will unlock when access expands.",
  "settings.enable": "Use my AI provider",
  "settings.enableHelp": "When off, all processing uses local browser rules.",
  "settings.baseUrl": "API base URL",
  "settings.model": "Model",
  "settings.apiKey": "API key",
  "settings.apiKeyPlaceholder": "Paste your key",
  "settings.showKey": "Show key",
  "settings.hideKey": "Hide key",
  "settings.save": "Save settings",
  "settings.saved": "Settings saved in this browser.",
  "settings.test": "Test connection",
  "settings.testing": "Testing…",
  "settings.connected": "Connection successful.",
  "settings.clearKey": "Clear key",
  "settings.securityTitle": "Important security note",
  "settings.securityBody":
    "Your key is stored only in this browser. Browser extensions and anyone with access to this browser profile may be able to read it. Use a restricted key with a spending limit.",
  "settings.cors":
    "Your provider must allow browser requests (CORS). If it does not, use a trusted local compatible endpoint.",
  "settings.invalidUrl": "Use HTTPS, or HTTP only for localhost.",
  "settings.theme": "Appearance",
  "settings.light": "Light",
  "settings.dark": "Dark",
  "settings.system": "System",
  "privacy.title": "Stored on this device",
  "privacy.body":
    "Meetings, transcripts, summaries, skills, preferences, and your API configuration stay in this browser profile. There is no Deskflow account or remote database.",
  "privacy.meetingCount": "meetings stored",
  "privacy.todoCount": "to-dos stored",
  "privacy.export": "Export all data",
  "privacy.delete": "Delete all local data",
  "privacy.deleteConfirm":
    "Permanently delete all Deskflow meetings, skills, settings, and your API key from this browser?",
  "privacy.deleted": "All local Deskflow data was deleted.",
  "privacy.aiTitle": "When data leaves your browser",
  "privacy.aiBody":
    "When your AI provider is enabled, only the text you submit is sent directly to the configured endpoint. Deskflow does not proxy or retain it remotely.",
  "privacy.storageTitle": "Browser storage limitations",
  "privacy.storageBody":
    "Clearing site data, using private browsing, or changing browser profiles can remove this workspace. Export a backup when the data matters.",
  "common.cancel": "Cancel",
  "common.close": "Close",
  "common.delete": "Delete",
  "common.error": "Something went wrong. Please try again.",
  "common.today": "TODAY",
};

type TranslationKey = keyof typeof en;

const zh: Record<TranslationKey, string> = {
  "app.name": "Deskflow",
  "app.tagline": "你的智能办公助手",
  "nav.dashboard": "概览",
  "nav.meetings": "会议纪要",
  "nav.calendar": "日历与待办",
  "nav.translate": "翻译",
  "nav.skills": "技能",
  "nav.search": "搜索",
  "nav.settings": "AI 设置",
  "nav.privacy": "隐私与数据",
  "nav.local": "本地优先",
  "nav.localDetail": "工作数据仅保存在此浏览器中",
  "nav.workspace": "个人工作空间",
  "nav.plan": "无需注册账号",
  "header.private": "隐私优先设计",
  "header.dashboard": "早上好",
  "header.meetings": "会议工作台",
  "header.calendar": "日历与待办",
  "header.translate": "翻译工作台",
  "header.skills": "可复用技能",
  "header.search": "智能搜索",
  "header.settings": "连接您自己的 AI",
  "header.privacy": "您的数据，由您掌控",
  "subtitle.dashboard": "查看今天最值得关注的事项。",
  "subtitle.meetings": "将会议文字稿整理为清晰、可核对的后续事项。",
  "subtitle.calendar": "在一个本地视图中安排日程并完成待办事项。",
  "subtitle.translate": "明确选择语言方向，并预览模拟翻译结果。",
  "subtitle.skills": "复用常用提示词，无需每次重新设计流程。",
  "subtitle.search": "使用模拟 AI 结果体验完整搜索流程。",
  "subtitle.settings": "直接在浏览器中连接您的 AI 服务商。",
  "subtitle.privacy": "清楚了解本地存储内容，并可随时删除。",
  "language.label": "语言",
  "language.english": "English",
  "language.chinese": "简体中文",
  "onboarding.eyebrow": "欢迎使用 DESKFLOW",
  "onboarding.title": "选择您的 AI 使用方式",
  "onboarding.description":
    "Deskflow 正在分阶段开放服务。现在可选择演示模式，先体验完整工作空间。",
  "onboarding.byok": "使用我自己的 API 密钥（BYOK）",
  "onboarding.byokDescription": "直接连接您偏好的兼容 AI 服务商。",
  "onboarding.hosted": "使用平台托管 API",
  "onboarding.hostedDescription": "无需自行管理服务商密钥，即可使用 Deskflow。",
  "onboarding.demo": "演示模式 / 先行体验",
  "onboarding.demoDescription": "进入工作空间，预览产品功能与使用流程。",
  "onboarding.comingSoon": "即将推出 / 内测中",
  "onboarding.tooltip": "即将推出",
  "onboarding.privacy": "您的选择仅保存在此浏览器中。",
  "demo.aiUnavailable":
    "请等待平台托管 API 上线，或在自有密钥功能开放后前往设置进行配置。",
  "dashboard.timeSaved": "预计节省时间",
  "dashboard.meetings": "已保存会议",
  "dashboard.meetingUnit": "个会议",
  "dashboard.actions": "待办事项",
  "dashboard.processing": "处理方式",
  "dashboard.aiSpend": "AI 消耗",
  "dashboard.demoMode": "演示模式",
  "dashboard.minutes": "分钟",
  "dashboard.local": "本地预览",
  "dashboard.ai": "您的 AI 服务商",
  "dashboard.recent": "最近会议",
  "dashboard.viewAll": "查看全部",
  "dashboard.emptyTitle": "工作空间已准备就绪",
  "dashboard.emptyBody": "导入第一份会议文字稿，即可生成结构化纪要和行动项。",
  "dashboard.newMeeting": "新建会议",
  "dashboard.actionsTitle": "行动项",
  "dashboard.noActions": "暂无待办事项。",
  "dashboard.markDone": "标记为已完成",
  "meeting.new": "新建会议",
  "meeting.saved": "已保存会议",
  "meeting.search": "搜索会议",
  "meeting.searchPlaceholder": "搜索标题或摘要…",
  "meeting.none": "没有符合条件的会议。",
  "meeting.title": "会议标题",
  "meeting.titlePlaceholder": "例如：每周产品评审",
  "meeting.transcript": "会议文字稿",
  "meeting.transcriptPlaceholder": "在此粘贴会议文字稿，或导入文本文件…",
  "meeting.import": "导入文件",
  "meeting.generate": "生成会议纪要",
  "meeting.generating": "正在生成纪要…",
  "meeting.smartExtract": "智能提取",
  "meeting.smartExtractHelp": "使用本地模拟逻辑识别行动项、负责人和截止日期。",
  "meeting.extractButton": "提取实体",
  "meeting.extractRequired": "请先输入一些会议文字稿再进行提取。",
  "meeting.extracted": "已提取模拟实体，请在下方检查并编辑。",
  "meeting.mockEntities": "模拟智能识别",
  "meeting.extractedItems": "已提取行动项",
  "meeting.action": "行动项",
  "meeting.owner": "负责人",
  "meeting.deadline": "截止日期",
  "meeting.addItem": "添加一项",
  "meeting.addToTodoList": "添加到待办清单",
  "meeting.addedToTodos": "已将提取内容添加到本地待办清单。",
  "meeting.mockHint": "演示处理使用确定性的模拟逻辑，不会调用任何外部 API。",
  "meeting.localHint": "当前为演示模式。AI 生成功能将在扩大开放后解锁。",
  "meeting.aiHint": "内容将直接发送至您配置的 AI 服务商。",
  "meeting.required": "请输入标题和至少 20 个字符的会议文字稿。",
  "meeting.created": "会议纪要已保存在本地。",
  "meeting.failed": "无法生成会议纪要。",
  "meeting.summary": "执行摘要",
  "meeting.decisions": "已确认决策",
  "meeting.actions": "行动项",
  "meeting.risks": "风险与阻碍",
  "meeting.noDecisions": "未识别到明确决策。",
  "meeting.noRisks": "未识别到明确风险。",
  "meeting.unassigned": "未分配",
  "meeting.review": "AI 辅助生成的纪要可能有误，分享前请务必核对。",
  "meeting.delete": "删除会议",
  "meeting.deleteConfirm": "要从此浏览器中删除该会议及其文字稿吗？",
  "meeting.export": "导出 Markdown",
  "meeting.back": "返回会议列表",
  "meeting.source": "来源",
  "meeting.localGenerated": "由本地规则生成",
  "meeting.aiGenerated": "由您的 AI 服务商生成",
  "meeting.accept": "接受",
  "meeting.edit": "编辑",
  "meeting.ignore": "忽略",
  "meeting.saveEdit": "保存修改",
  "meeting.addToTodo": "添加到待办",
  "meeting.addedToTodo": "已添加到待办",
  "meeting.reviewSummary": "审核摘要",
  "meeting.summaryAdded": "摘要审核任务已添加到本地待办清单。",
  "meeting.status.pending": "待审核",
  "meeting.status.accepted": "已接受",
  "meeting.status.ignored": "已忽略",
  "calendar.today": "今天",
  "calendar.zoom": "日历缩放",
  "calendar.zoomIn": "放大",
  "calendar.zoomOut": "缩小",
  "calendar.selectedDay": "所选日期",
  "calendar.empty": "当天没有日程或待办。",
  "calendar.complete": "标记为完成",
  "calendar.allDay": "全天",
  "calendar.add": "快速添加",
  "calendar.titlePlaceholder": "待办或日程标题",
  "calendar.ownerPlaceholder": "负责人",
  "calendar.todo": "待办",
  "calendar.schedule": "日程",
  "calendar.addButton": "保存到本地",
  "calendar.saved": "已保存在此浏览器中。",
  "translate.pair": "语言方向",
  "translate.enZh": "英文 → 中文",
  "translate.zhEn": "中文 → 英文",
  "translate.swap": "交换方向",
  "translate.source": "原文",
  "translate.result": "模拟译文",
  "translate.placeholder": "粘贴需要翻译的内容…",
  "translate.empty": "模拟翻译结果将在这里显示。",
  "translate.translate": "使用模拟翻译",
  "translate.clear": "清空",
  "translate.copy": "复制译文",
  "translate.copied": "译文已复制。",
  "translate.mockNotice": "已在本地完成模拟翻译。",
  "translate.localOnly": "本地模拟历史",
  "translate.history": "最近翻译",
  "translate.noHistory": "暂无翻译记录。",
  "skills.starter": "常用技能",
  "skills.input": "输入内容",
  "skills.inputPlaceholder": "粘贴您希望处理的文本…",
  "skills.run": "运行技能",
  "skills.running": "正在运行…",
  "skills.output": "处理结果",
  "skills.choose": "选择一个技能即可开始。",
  "skills.localNotice": "该结果由本地模拟逻辑生成，不会发送 API 请求。",
  "skills.mockBadge": "本地模拟",
  "skills.useExample": "使用示例文本",
  "skills.summarize.name": "总结文档",
  "skills.summarize.description": "将较长的商务文本浓缩为核心要点。",
  "skills.email.name": "润色邮件",
  "skills.email.description": "让邮件更简洁、专业且友好。",
  "skills.actions.name": "提取行动项",
  "skills.actions.description": "从非结构化笔记中识别任务、负责人和日期。",
  "skills.report.name": "生成周报",
  "skills.report.description": "将零散进展整理为清晰的每周状态报告。",
  "skills.translate.name": "翻译商务文本",
  "skills.translate.description": "在保持专业语气的同时翻译文本。",
  "skills.agenda.name": "准备会议议程",
  "skills.agenda.description": "根据目标和背景资料生成会议议程。",
  "search.label": "搜索关键词",
  "search.placeholder": "搜索主题、公司或工作流程…",
  "search.button": "搜索",
  "search.helper": "搜索结果和 AI 摘要均为用于流程测试的硬编码模拟内容。",
  "search.recent": "最近搜索",
  "search.emptyTitle": "搜索演示知识空间",
  "search.emptyBody": "输入任意关键词，即可预览结果卡片和模拟 AI 摘要。",
  "search.mockLabel": "模拟搜索结果",
  "search.resultsFor": "搜索结果",
  "search.aiSummary": "AI 摘要",
  "search.more": "展开模拟详情",
  "search.less": "收起详情",
  "search.mockDetail":
    "此展开区域为本地模拟内容。未来接入实时搜索服务时，无需改变当前界面结构。",
  "settings.provider": "AI 服务商",
  "settings.comingSoon": "BYOK 目前处于内测阶段，扩大开放后即可使用以下配置。",
  "settings.enable": "使用我的 AI 服务商",
  "settings.enableHelp": "关闭后，所有处理均使用浏览器本地规则。",
  "settings.baseUrl": "API 基础地址",
  "settings.model": "模型",
  "settings.apiKey": "API 密钥",
  "settings.apiKeyPlaceholder": "粘贴您的密钥",
  "settings.showKey": "显示密钥",
  "settings.hideKey": "隐藏密钥",
  "settings.save": "保存设置",
  "settings.saved": "设置已保存在此浏览器中。",
  "settings.test": "测试连接",
  "settings.testing": "正在测试…",
  "settings.connected": "连接成功。",
  "settings.clearKey": "清除密钥",
  "settings.securityTitle": "重要安全提示",
  "settings.securityBody":
    "您的密钥仅保存在此浏览器中。浏览器扩展以及能访问此浏览器配置文件的人可能读取该密钥。请使用受限且设有消费上限的密钥。",
  "settings.cors":
    "您的服务商必须允许浏览器跨域请求（CORS）。如不支持，请使用您信任的本地兼容接口。",
  "settings.invalidUrl": "请使用 HTTPS；仅 localhost 可使用 HTTP。",
  "settings.theme": "外观",
  "settings.light": "浅色",
  "settings.dark": "深色",
  "settings.system": "跟随系统",
  "privacy.title": "保存在本设备上的数据",
  "privacy.body":
    "会议、文字稿、摘要、技能、偏好设置和 API 配置均保存在此浏览器配置文件中。Deskflow 不要求账号，也不使用远程数据库。",
  "privacy.meetingCount": "个会议已保存",
  "privacy.todoCount": "个待办已保存",
  "privacy.export": "导出全部数据",
  "privacy.delete": "删除全部本地数据",
  "privacy.deleteConfirm":
    "确定要从此浏览器永久删除全部 Deskflow 会议、技能、设置和 API 密钥吗？",
  "privacy.deleted": "全部 Deskflow 本地数据已删除。",
  "privacy.aiTitle": "数据何时会离开浏览器",
  "privacy.aiBody":
    "启用 AI 服务商后，只有您提交的文本会直接发送至所配置的接口。Deskflow 不会通过远程服务器中转或保存这些内容。",
  "privacy.storageTitle": "浏览器存储的局限",
  "privacy.storageBody":
    "清除网站数据、使用无痕模式或更换浏览器配置文件都可能移除此工作空间。重要数据请及时导出备份。",
  "common.cancel": "取消",
  "common.close": "关闭",
  "common.delete": "删除",
  "common.error": "出现问题，请重试。",
  "common.today": "今天",
};

interface I18nContextValue {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: TranslationKey) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  const [language, setLanguageState] = useState<Language>(() =>
    localStorage.getItem("deskflow.language") === "zh-CN" ? "zh-CN" : "en",
  );

  const setLanguage = (next: Language): void => {
    localStorage.setItem("deskflow.language", next);
    setLanguageState(next);
  };

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const value = useMemo<I18nContextValue>(
    () => ({
      language,
      setLanguage,
      t: (key) => (language === "zh-CN" ? zh[key] : en[key]),
    }),
    [language],
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error("useI18n must be used inside I18nProvider");
  return value;
}

export const translationCatalogs = { en, "zh-CN": zh } as const;
